import { jsonLdGraph, organizationNode, websiteNode, type JsonLdNode } from '~/utils/schemaOrg';
import { toE164, trimTrailingSlash } from '~/utils/seo';

const WEEKDAYS = [
    ['monday', 'Monday'],
    ['tuesday', 'Tuesday'],
    ['wednesday', 'Wednesday'],
    ['thursday', 'Thursday'],
    ['friday', 'Friday'],
    ['saturday', 'Saturday'],
    ['sunday', 'Sunday'],
] as const;

const TIME_PATTERN = /^\d{1,2}:\d{2}$/;

function openingHoursSpecification(openingHours: any): JsonLdNode[] {
    if (!openingHours) return [];

    return WEEKDAYS.map(([key, dayOfWeek]) => {
        const day = openingHours[key];
        if (!TIME_PATTERN.test(day?.openingTime ?? '') || !TIME_PATTERN.test(day?.closingTime ?? '')) return undefined;
        return {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: `https://schema.org/${dayOfWeek}`,
            opens: day.openingTime,
            closes: day.closingTime,
        } as JsonLdNode;
    }).filter(Boolean) as JsonLdNode[];
}


export default function useSiteSchema(siteConfig: any) {
    const config = useRuntimeConfig();
    const prodUrl = trimTrailingSlash(String(config.public.prodUrl ?? ''));
    if (!prodUrl || !siteConfig) return;

    const siteUrl = `${prodUrl}/nl/`;
    const name = siteConfig.title || 'Ballegooyen Modes';
    // Sanity's logo field is optional; the shipped /logo.svg is the same mark the header renders.
    const logoUrl = siteConfig.logo
        ? useImageUrl(siteConfig.logo).format('webp').width(512).url()
        : `${prodUrl}/logo.svg`;

    const graph = jsonLdGraph([
        websiteNode({
            siteUrl,
            name,
            searchUrlTemplate: `${prodUrl}/search?tn_q={search_term_string}`,
        }),
        organizationNode({
            siteUrl,
            name,
            logoUrl,
            telephone: toE164(siteConfig.phone),
            email: siteConfig.email,
            address: {
                street: siteConfig.address,
                postalCode: siteConfig.zipcode,
                city: siteConfig.city,
            },
            sameAs: [
                siteConfig.facebook,
                siteConfig.instagram,
                siteConfig.tiktok,
                siteConfig.linkedin,
                siteConfig.youtube,
            ],
            openingHoursSpecification: openingHoursSpecification(siteConfig.openingHours),
        }),
    ]);

    useHead({
        script: [
            {
                key: 'schema-org-site',
                type: 'application/ld+json',
                innerHTML: graph,
            },
        ],
    });
}
